import { useCallback, useEffect, useMemo } from 'react'
import { useAlert } from 'react-alert'
import { useTranslation } from 'react-i18next'
import { FileInputChangeListener } from '../../hooks/useFileInput'

interface UseFileInputWithAcceptParams {
  listener: FileInputChangeListener
  multiple?: boolean
  accept?: string
}

interface UseFileInputWithAcceptReturn {
  handleFileUploadClick: () => void
}

/**
 * Beck-specific extension of the SF useFileInput hook.
 * Adds the optional `accept` parameter to restrict the file types
 * shown in the browser's file-picker dialog.
 *
 * The server still runs its own validation (sfCheckFiletypes);
 * this is a UX improvement only.
 *
 * Example:
 *   useFileInputWithAccept({ listener, accept: '.docx,.doc' })
 */
export const useFileInputWithAccept = ({
  listener,
  multiple = false,
  accept,
}: UseFileInputWithAcceptParams): UseFileInputWithAcceptReturn => {
  const alert = useAlert()
  const { t } = useTranslation()

  const inputElement = useMemo(() => {
    const element = globalThis.document.createElement('input')
    element.setAttribute('type', 'file')
    if (multiple) { element.setAttribute('multiple', 'multiple') }
    if (accept !== undefined) { element.setAttribute('accept', accept) }
    return element
  }, [multiple, accept])

  const handleFileUploadClick =
    useCallback(() => { inputElement.click() }, [inputElement])

  const wrappedListener = useCallback((event: Event) => {
    const files = inputElement.files
    if (files && accept) {
      const allowedRules = accept.split(',').map(rule => rule.trim().toLowerCase())

      const hasInvalidFile = Array.from(files).some(file => {
        const fileName = file.name.toLowerCase()
        const fileType = file.type.toLowerCase()

        return !allowedRules.some(rule => {
          if (rule.startsWith('.')) {
            return fileName.endsWith(rule)
          } else if (rule.endsWith('/*')) {
            const prefix = rule.slice(0, -2)
            return fileType.startsWith(prefix)
          } else {
            return fileType === rule
          }
        })
      })

      if (hasInvalidFile) {
        alert.error(t('cst.upload.invalidFileType'))
        inputElement.value = ''
        return
      }
    }

    listener.call(inputElement, event)
  }, [inputElement, accept, listener, alert, t])

  useEffect(() => {
    inputElement.addEventListener('change', wrappedListener)
    return () => { inputElement.removeEventListener('change', wrappedListener) }
  }, [inputElement, wrappedListener])

  return { handleFileUploadClick }
}
